import React, { useState, useEffect, useCallback, startTransition } from 'react';
import { apiClient } from '../utils/apiClient';
import { useCart } from '../contexts/CartContext';
import { useTheme } from '../contexts/ThemeContext';
import { useProfile } from '../contexts/ProfileContext';
import './ProfileSelection.css';

const ProfileSelection = React.memo(({ 
  profile, 
  onUpdate, 
  isEditing = false, 
  onCancel 
}) => {
  const [availableItems, setAvailableItems] = useState({ mcpServers: [], workflows: [] });
  const [profileItems, setProfileItems] = useState({ mcpServers: [], workflows: [] });
  const [selectedItems, setSelectedItems] = useState({
    available: { mcpServers: [], workflows: [] },
    cart: { mcpServers: [], workflows: [] },
    profile: { mcpServers: [], workflows: [] }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { isDarkMode } = useTheme();
  
  // Use global cart context
  const { 
    cart, 
    addMCPServerToCart, 
    addWorkflowToCart, 
    removeMCPServerFromCart, 
    removeWorkflowFromCart,
    addMultipleItemsToCart,
    removeMultipleItemsFromCart,
    addMultipleItemsToActiveProfile
  } = useCart();
  
  // Use global profile context
  const { refreshProfile } = useProfile();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/profiles/available-items');
      const allItems = response.data;
      
      setAvailableItems({
        mcpServers: allItems.mcpServers,
        workflows: allItems.workflows
      });
    } catch (error) {
      console.error('Error loading available items:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (profile) {
      setProfileItems({
        mcpServers: profile.mcpServers?.map(item => item.mcpServer) || [],
        workflows: profile.workflows?.map(item => item.workflow) || []
      });
    }
  }, [profile]);

  const moveToCart = async (type, item) => {
    try {
      setAvailableItems(prev => ({
        ...prev,
        [type]: prev[type].filter(i => i.id !== item.id)
      }));
      
      if (type === 'mcpServers') {
        const result = await addMCPServerToCart(item.id);
        if (!result.success) {
          // Revert the UI change if API call failed
          setAvailableItems(prev => ({
            ...prev,
            [type]: [...prev[type], item]
          }));
          alert(result.error);
        }
      } else if (type === 'workflows') {
        const result = await addWorkflowToCart(item.id);
        if (!result.success) {
          // Revert the UI change if API call failed
          setAvailableItems(prev => ({
            ...prev,
            [type]: [...prev[type], item]
          }));
          alert(result.error);
        }
      }
    } catch (error) {
      console.error('Error moving to cart:', error);
      // Revert the UI change
      setAvailableItems(prev => ({
        ...prev,
        [type]: [...prev[type], item]
      }));
    }
  };

  const moveFromCart = async (type, item) => {
    try {
      setAvailableItems(prev => ({
        ...prev,
        [type]: [...prev[type], item]
      }));
      
      if (type === 'mcpServers') {
        const result = await removeMCPServerFromCart(item.id);
        if (!result.success) {
          // Revert the UI change if API call failed
          setAvailableItems(prev => ({
            ...prev,
            [type]: prev[type].filter(i => i.id !== item.id)
          }));
          alert(result.error);
        }
      } else if (type === 'workflows') {
        const result = await removeWorkflowFromCart(item.id);
        if (!result.success) {
          // Revert the UI change if API call failed
          setAvailableItems(prev => ({
            ...prev,
            [type]: prev[type].filter(i => i.id !== item.id)
          }));
          alert(result.error);
        }
      }
    } catch (error) {
      console.error('Error moving from cart:', error);
      // Revert the UI change
      setAvailableItems(prev => ({
        ...prev,
        [type]: prev[type].filter(i => i.id !== item.id)
      }));
    }
  };

  const moveToProfile = async (type, item) => {
    setProfileItems(prev => ({
      ...prev,
      [type]: [...prev[type], item]
    }));
    
    if (type === 'mcpServers') {
      await removeMCPServerFromCart(item.id);
      // Refresh profile context to update the + Profile button state
      await refreshProfile();
    } else if (type === 'workflows') {
      await removeWorkflowFromCart(item.id);
    }
  };

  const moveFromProfile = async (type, item) => {
    setProfileItems(prev => ({
      ...prev,
      [type]: prev[type].filter(i => i.id !== item.id)
    }));
    
    if (type === 'mcpServers') {
      await addMCPServerToCart(item.id);
      // Refresh profile context to update the + Profile button state
      await refreshProfile();
    } else if (type === 'workflows') {
      await addWorkflowToCart(item.id);
    }
  };


  // Checkbox handling functions
  const toggleItemSelection = (column, type, item) => {
    setSelectedItems(prev => {
      const currentSelection = prev[column][type];
      const isSelected = currentSelection.some(selected => selected.id === item.id);
      
      if (isSelected) {
        return {
          ...prev,
          [column]: {
            ...prev[column],
            [type]: currentSelection.filter(selected => selected.id !== item.id)
          }
        };
      } else {
        return {
          ...prev,
          [column]: {
            ...prev[column],
            [type]: [...currentSelection, item]
          }
        };
      }
    });
  };

  const toggleSelectAll = (column, type, items) => {
    const allSelected = items.every(item => 
      selectedItems[column][type].some(selected => selected.id === item.id)
    );
    
    if (allSelected) {
      // Deselect all
      setSelectedItems(prev => ({
        ...prev,
        [column]: {
          ...prev[column],
          [type]: []
        }
      }));
    } else {
      // Select all
      setSelectedItems(prev => ({
        ...prev,
        [column]: {
          ...prev[column],
          [type]: [...items]
        }
      }));
    }
  };

  const isItemSelected = (column, type, item) => {
    return selectedItems[column][type].some(selected => selected.id === item.id);
  };

  const getSelectedCount = (column, type) => {
    return selectedItems[column][type].length;
  };

  // Move selected items functions

  // Batched transfer function for multiple types
  const moveSelectedToCartBatched = useCallback(async () => {
    const mcpServersToMove = selectedItems.available.mcpServers;
    const workflowsToMove = selectedItems.available.workflows;
    
    if (mcpServersToMove.length === 0 && workflowsToMove.length === 0) return;

    try {
      // Add all items to cart in a single batched operation
      const mcpServerIds = mcpServersToMove.map(item => item.id);
      const workflowIds = workflowsToMove.map(item => item.id);
      
      const result = await addMultipleItemsToCart(mcpServerIds, workflowIds);
      if (!result.success) {
        alert(result.error);
        return;
      }
      
      // Single state update to minimize re-renders using startTransition
      startTransition(() => {
        setAvailableItems(prev => ({
          mcpServers: prev.mcpServers.filter(item => 
            !mcpServersToMove.some(selected => selected.id === item.id)
          ),
          workflows: prev.workflows.filter(item => 
            !workflowsToMove.some(selected => selected.id === item.id)
          )
        }));
        
        setSelectedItems(prev => ({
          ...prev,
          available: {
            mcpServers: [],
            workflows: []
          }
        }));
      });
    } catch (error) {
      console.error('Error moving selected items to cart:', error);
    }
  }, [selectedItems.available, addMultipleItemsToCart]);


  // Batched transfer function for multiple types from cart
  const moveSelectedFromCartBatched = useCallback(async () => {
    const mcpServersToMove = selectedItems.cart.mcpServers;
    const workflowsToMove = selectedItems.cart.workflows;
    
    if (mcpServersToMove.length === 0 && workflowsToMove.length === 0) return;

    try {
      // Remove all items from cart in a single batched operation
      const mcpServerIds = mcpServersToMove.map(item => item.id);
      const workflowIds = workflowsToMove.map(item => item.id);
      
      const result = await removeMultipleItemsFromCart(mcpServerIds, workflowIds);
      if (!result.success) {
        alert(result.error);
        return;
      }
      
      // Batch all state updates together to minimize re-renders using startTransition
      startTransition(() => {
        setAvailableItems(prev => ({
          mcpServers: [...prev.mcpServers, ...mcpServersToMove],
          workflows: [...prev.workflows, ...workflowsToMove]
        }));
        
        setSelectedItems(prev => ({
          ...prev,
          cart: {
            mcpServers: [],
            workflows: []
          }
        }));
      });
    } catch (error) {
      console.error('Error moving selected items from cart:', error);
    }
  }, [selectedItems.cart, removeMultipleItemsFromCart]);


  // Batched transfer function for multiple types from cart to profile
  const moveSelectedToProfileBatched = useCallback(async () => {
    const mcpServersToMove = selectedItems.cart.mcpServers;
    const workflowsToMove = selectedItems.cart.workflows;
    
    if (mcpServersToMove.length === 0 && workflowsToMove.length === 0) return;

    try {
      // Add all items to profile in a single batched operation (handles duplicates gracefully)
      const mcpServerIds = mcpServersToMove.map(item => item.id);
      const workflowIds = workflowsToMove.map(item => item.id);
      
      const result = await addMultipleItemsToActiveProfile(mcpServerIds, workflowIds);
      if (!result.success) {
        alert(result.error);
        return;
      }
      
      // Remove all items from cart in a single batched operation
      const removeResult = await removeMultipleItemsFromCart(mcpServerIds, workflowIds);
      if (!removeResult.success) {
        console.warn('Some items could not be removed from cart:', removeResult.error);
        // Don't fail the entire operation if cart removal fails
      }
      
      // Batch all state updates together to minimize re-renders using startTransition
      startTransition(() => {
        setProfileItems(prev => ({
          mcpServers: [...prev.mcpServers, ...mcpServersToMove],
          workflows: [...prev.workflows, ...workflowsToMove]
        }));
        
        setSelectedItems(prev => ({
          ...prev,
          cart: {
            mcpServers: [],
            workflows: []
          }
        }));
      });
      
      // Refresh profile context to update the + Profile button state
      await refreshProfile();
    } catch (error) {
      console.error('Error moving selected items to profile:', error);
    }
  }, [selectedItems.cart, addMultipleItemsToActiveProfile, removeMultipleItemsFromCart, refreshProfile]);


  // Batched transfer function for multiple types from profile to cart
  const moveSelectedFromProfileBatched = useCallback(async () => {
    const mcpServersToMove = selectedItems.profile.mcpServers;
    const workflowsToMove = selectedItems.profile.workflows;
    
    if (mcpServersToMove.length === 0 && workflowsToMove.length === 0) return;

    try {
      // Add all items to cart in a single batched operation
      const mcpServerIds = mcpServersToMove.map(item => item.id);
      const workflowIds = workflowsToMove.map(item => item.id);
      
      const result = await addMultipleItemsToCart(mcpServerIds, workflowIds);
      if (!result.success) {
        alert(result.error);
        return;
      }
      
      // Batch all state updates together to minimize re-renders using startTransition
      startTransition(() => {
        setProfileItems(prev => ({
          mcpServers: prev.mcpServers.filter(item => 
            !mcpServersToMove.some(selected => selected.id === item.id)
          ),
          workflows: prev.workflows.filter(item => 
            !workflowsToMove.some(selected => selected.id === item.id)
          )
        }));
        
        setSelectedItems(prev => ({
          ...prev,
          profile: {
            mcpServers: [],
            workflows: []
          }
        }));
      });
      
      // Refresh profile context to update the + Profile button state
      await refreshProfile();
    } catch (error) {
      console.error('Error moving selected items from profile:', error);
    }
  }, [selectedItems.profile, addMultipleItemsToCart, refreshProfile]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const mcpServerIds = profileItems.mcpServers.map(item => item.id);
      const workflowIds = profileItems.workflows.map(item => item.id);

      await apiClient.put(`/profiles/${profile.id}`, {
        mcpServerIds,
        workflowIds
      });

      // Refresh profile context to update the + Profile button state
      await refreshProfile();
      onUpdate();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setProfileItems({
        mcpServers: profile.mcpServers?.map(item => item.mcpServer) || [],
        workflows: profile.workflows?.map(item => item.workflow) || []
      });
    }
    // Don't clear the global cart - it persists across profile editing
    onCancel();
  };

  if (loading) {
    return <div className="profile-selection-loading">Loading...</div>;
  }

  return (
    <div className={`profile-selection ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="profile-selection-header">
        <h3>Profile Configuration</h3>
        {isEditing && (
          <div className="profile-selection-actions">
            <button 
              className="btn btn-secondary" 
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      <div className="profile-selection-grid">
        {/* Available Items */}
        <div className="selection-column">
          <div className="selection-header">
            <h4>Available</h4>
            <button 
              className="btn btn-sm btn-primary arrow-btn"
              onClick={moveSelectedToCartBatched}
              disabled={getSelectedCount('available', 'mcpServers') === 0 && getSelectedCount('available', 'workflows') === 0}
              title="Move Selected to Cart"
            >
              &gt;&gt;&gt;
            </button>
          </div>
          
          <div className="selection-section">
            <div className="section-header">
              <h5>MCP Servers</h5>
            </div>
            <div className="select-all-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={availableItems.mcpServers.length > 0 && availableItems.mcpServers.every(item => isItemSelected('available', 'mcpServers', item))}
                  onChange={() => toggleSelectAll('available', 'mcpServers', availableItems.mcpServers)}
                  disabled={availableItems.mcpServers.length === 0}
                />
                Select All
              </label>
            </div>
            <div className="item-list">
              {availableItems.mcpServers.map(item => (
                <div key={item.id} className="item-card">
                  <div className="item-checkbox">
                    <input
                      type="checkbox"
                      checked={isItemSelected('available', 'mcpServers', item)}
                      onChange={() => toggleItemSelection('available', 'mcpServers', item)}
                    />
                  </div>
                  <div className="item-info">
                    <strong>{item.name}</strong>
                    <p>{item.description}</p>
                    <span className={`status status-${item.status.toLowerCase()}`}>
                      {item.status}
                    </span>
                  </div>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => moveToCart('mcpServers', item)}
                    >
                      &gt;
                    </button>
                </div>
              ))}
            </div>
          </div>

          <div className="selection-section">
            <div className="section-header">
              <h5>Workflows</h5>
            </div>
            <div className="select-all-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={availableItems.workflows.length > 0 && availableItems.workflows.every(item => isItemSelected('available', 'workflows', item))}
                  onChange={() => toggleSelectAll('available', 'workflows', availableItems.workflows)}
                  disabled={availableItems.workflows.length === 0}
                />
                Select All
              </label>
            </div>
            <div className="item-list">
              {availableItems.workflows.map(item => (
                <div key={item.id} className="item-card">
                  <div className="item-checkbox">
                    <input
                      type="checkbox"
                      checked={isItemSelected('available', 'workflows', item)}
                      onChange={() => toggleItemSelection('available', 'workflows', item)}
                    />
                  </div>
                  <div className="item-info">
                    <strong>{item.name}</strong>
                    <p>{item.description}</p>
                  </div>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => moveToCart('workflows', item)}
                    >
                      &gt;
                    </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cart */}
        <div className="selection-column">
          <div className="selection-header">
            <h4>Cart</h4>
            <div className="cart-actions">
              <button 
                className="btn btn-sm btn-success arrow-btn"
                onClick={moveSelectedFromCartBatched}
                disabled={getSelectedCount('cart', 'mcpServers') === 0 && getSelectedCount('cart', 'workflows') === 0}
                title="Move Selected to Available"
              >
                &lt;&lt;&lt;
              </button>
              <button 
                className="btn btn-sm btn-primary arrow-btn"
                onClick={moveSelectedToProfileBatched}
                disabled={getSelectedCount('cart', 'mcpServers') === 0 && getSelectedCount('cart', 'workflows') === 0}
                title="Move Selected to Profile"
              >
                &gt;&gt;&gt;
              </button>
            </div>
          </div>
          
          <div className="selection-section">
            <div className="section-header">
              <h5>MCP Servers</h5>
            </div>
            <div className="select-all-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={(cart?.mcpServers || []).length > 0 && (cart?.mcpServers || []).every(item => isItemSelected('cart', 'mcpServers', item))}
                  onChange={() => toggleSelectAll('cart', 'mcpServers', cart?.mcpServers || [])}
                  disabled={(cart?.mcpServers || []).length === 0}
                />
                Select All
              </label>
            </div>
            <div className="item-list">
              {(cart?.mcpServers || []).map(item => (
                <div key={item.id} className="item-card">
                  <div className="item-checkbox">
                    <input
                      type="checkbox"
                      checked={isItemSelected('cart', 'mcpServers', item)}
                      onChange={() => toggleItemSelection('cart', 'mcpServers', item)}
                    />
                  </div>
                  <div className="item-info">
                    <strong>{item.name}</strong>
                    <p>{item.description}</p>
                    <span className={`status status-${item.status.toLowerCase()}`}>
                      {item.status}
                    </span>
                  </div>
                  <div className="item-actions">
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => moveFromCart('mcpServers', item)}
                    >
                      &lt;
                    </button>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => moveToProfile('mcpServers', item)}
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="selection-section">
            <div className="section-header">
              <h5>Workflows</h5>
            </div>
            <div className="select-all-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={(cart?.workflows || []).length > 0 && (cart?.workflows || []).every(item => isItemSelected('cart', 'workflows', item))}
                  onChange={() => toggleSelectAll('cart', 'workflows', cart?.workflows || [])}
                  disabled={(cart?.workflows || []).length === 0}
                />
                Select All
              </label>
            </div>
            <div className="item-list">
              {(cart?.workflows || []).map(item => (
                <div key={item.id} className="item-card">
                  <div className="item-checkbox">
                    <input
                      type="checkbox"
                      checked={isItemSelected('cart', 'workflows', item)}
                      onChange={() => toggleItemSelection('cart', 'workflows', item)}
                    />
                  </div>
                  <div className="item-info">
                    <strong>{item.name}</strong>
                    <p>{item.description}</p>
                  </div>
                  <div className="item-actions">
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => moveFromCart('workflows', item)}
                    >
                      &lt;
                    </button>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => moveToProfile('workflows', item)}
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Profile Items */}
        <div className="selection-column">
          <div className="selection-header">
            <h4>Profile</h4>
            <button 
              className="btn btn-sm btn-success arrow-btn"
              onClick={moveSelectedFromProfileBatched}
              disabled={getSelectedCount('profile', 'mcpServers') === 0 && getSelectedCount('profile', 'workflows') === 0}
              title="Move Selected to Cart"
            >
              &lt;&lt;&lt;
            </button>
          </div>
          
          <div className="selection-section">
            <div className="section-header">
              <h5>MCP Servers</h5>
            </div>
            <div className="select-all-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={profileItems.mcpServers.length > 0 && profileItems.mcpServers.every(item => isItemSelected('profile', 'mcpServers', item))}
                  onChange={() => toggleSelectAll('profile', 'mcpServers', profileItems.mcpServers)}
                  disabled={profileItems.mcpServers.length === 0}
                />
                Select All
              </label>
            </div>
            <div className="item-list">
              {profileItems.mcpServers.map(item => (
                <div key={item.id} className="item-card">
                  <div className="item-checkbox">
                    <input
                      type="checkbox"
                      checked={isItemSelected('profile', 'mcpServers', item)}
                      onChange={() => toggleItemSelection('profile', 'mcpServers', item)}
                    />
                  </div>
                  <div className="item-info">
                    <strong>{item.name}</strong>
                    <p>{item.description}</p>
                    <span className={`status status-${item.status.toLowerCase()}`}>
                      {item.status}
                    </span>
                  </div>
                  <button 
                    className="btn btn-sm btn-success"
                    onClick={() => moveFromProfile('mcpServers', item)}
                  >
                    &lt;
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="selection-section">
            <div className="section-header">
              <h5>Workflows</h5>
            </div>
            <div className="select-all-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={profileItems.workflows.length > 0 && profileItems.workflows.every(item => isItemSelected('profile', 'workflows', item))}
                  onChange={() => toggleSelectAll('profile', 'workflows', profileItems.workflows)}
                  disabled={profileItems.workflows.length === 0}
                />
                Select All
              </label>
            </div>
            <div className="item-list">
              {profileItems.workflows.map(item => (
                <div key={item.id} className="item-card">
                  <div className="item-checkbox">
                    <input
                      type="checkbox"
                      checked={isItemSelected('profile', 'workflows', item)}
                      onChange={() => toggleItemSelection('profile', 'workflows', item)}
                    />
                  </div>
                  <div className="item-info">
                    <strong>{item.name}</strong>
                    <p>{item.description}</p>
                  </div>
                  <button 
                    className="btn btn-sm btn-success"
                    onClick={() => moveFromProfile('workflows', item)}
                  >
                    &lt;
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ProfileSelection;
